/**
 * Paperless-ngx API Client
 *
 * Kommuniziert in Production über den FastAPI-Proxy (ortofapi).
 * In Dev: Vite-Proxy leitet /paperless-api → https://paperless.administer.cloud/api
 */

import { API_BASE } from '../apiBase'

// ── Konfiguration ──────────────────────────────────────────────────────
const IS_DEV = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')

// Dev: direkter Zugriff via Vite-Proxy
// Prod: alle Aufrufe gehen über FastAPI-Proxy → API_BASE + /paperless/...
const PAPERLESS_TOKEN = 'fadb4dffd520fc9a8148ea55fc908be047ab6ca0'

const headers = (): HeadersInit => IS_DEV
  ? { Authorization: `Token ${PAPERLESS_TOKEN}` }
  : {} // Prod: Token wird vom FastAPI-Proxy gesetzt

const jsonHeaders = (): HeadersInit => ({
  ...headers(),
  'Content-Type': 'application/json',
})

// ── Types ──────────────────────────────────────────────────────────────

export interface PaperlessCustomField {
  field: number
  field_name?: string
  value: string | null
}

export interface PaperlessDocument {
  id: number
  title: string
  content: string
  original_file_name: string
  created: string
  created_date: string
  modified: string
  added: string
  archive_serial_number: number | null
  correspondent: number | null
  document_type: number | null
  tags: number[]
  notes: PaperlessNote[]
  custom_fields?: PaperlessCustomField[]
}

export interface PaperlessNote {
  id: number
  note: string
  created: string
  user: number
}

export interface PaperlessTag {
  id: number
  name: string
  slug: string
  color: string
  text_color: string
  is_inbox_tag: boolean
  matching_algorithm: number
}

export interface PaperlessCorrespondent {
  id: number
  name: string
  slug: string
}

export interface PaperlessDocumentType {
  id: number
  name: string
  slug: string
}

export interface PaperlessListResponse<T> {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

export interface PaperlessSearchParams {
  page?: number
  page_size?: number
  ordering?: string
  query?: string
  tags__id__in?: number[]
  correspondent__id?: number
  document_type__id?: number
}

/** Antwort von GET /paperless/search */
export interface PaperlessSearchResult {
  id: number
  title: string
  created: string
}

export interface PaperlessSearchResponse {
  query: string
  count: number
  results: PaperlessSearchResult[]
}

/** Antwort von POST /paperless/upload */
export interface PaperlessUploadResponse {
  task_id: string
  filename: string
  session_id: string
  indexing: string
}

/** Antwort von GET /paperless/tasks/{task_id} */
export interface PaperlessTaskStatus {
  task_id: string
  status: 'PENDING' | 'STARTED' | 'SUCCESS' | 'FAILURE'
  document_id?: number
  duplicate?: boolean
}

/** Antwort von GET /paperless/documents/{doc_id}/pages */
export interface PaperlessPageSearchResponse {
  doc_id: number
  query: string
  pages: number[]
}

// ── URL-Helfer ─────────────────────────────────────────────────────────

/**
 * Baut die korrekte URL für einen Paperless-API-Pfad.
 * Dev:  /paperless-api/documents/...   (Vite-Proxy)
 * Prod: https://ortofapi.administer.cloud/paperless/documents/...  (FastAPI-Proxy)
 */
function paperlessDocUrl(path: string): string {
  if (IS_DEV) return `/paperless-api${path}`
  return `${API_BASE}/paperless/documents${path}`
}

/**
 * Baut die korrekte URL für einen Paperless-Top-Level-Pfad (z.B. /paperless/upload).
 * Dev:  /paperless-api/...   (Vite-Proxy)
 * Prod: https://ortofapi.administer.cloud/paperless/...  (FastAPI-Proxy)
 */
function paperlessUrl(path: string): string {
  if (IS_DEV) return `/paperless-api${path}`
  return `${API_BASE}/paperless${path}`
}

// ── API-Funktionen ─────────────────────────────────────────────────────

/** Dokumente auflisten (paginiert) — GET /paperless/documents */
export async function fetchDocuments(
  params: PaperlessSearchParams = {}
): Promise<PaperlessListResponse<PaperlessDocument>> {
  const qp = new URLSearchParams()
  qp.set('page_size', String(params.page_size ?? 25))
  if (params.page) qp.set('page', String(params.page))
  if (params.ordering) qp.set('ordering', params.ordering)
  if (params.query) qp.set('search', params.query) // FastAPI nutzt 'search' statt 'query'
  if (params.tags__id__in?.length) {
    qp.set('tags__id__in', params.tags__id__in.join(','))
  }
  if (params.correspondent__id) {
    qp.set('correspondent__id', String(params.correspondent__id))
  }
  if (params.document_type__id) {
    qp.set('document_type__id', String(params.document_type__id))
  }

  const url = IS_DEV
    ? `/paperless-api/documents/?${qp.toString()}`
    : `${API_BASE}/paperless/documents?${qp.toString()}`

  const res = await fetch(url, { headers: headers() })
  if (!res.ok) throw new Error(`Paperless API error: ${res.status} ${res.statusText}`)
  return res.json()
}

/**
 * Dokumente laden, gefiltert nach dem Custom Field `paperless_tag_id` des aktuellen Projekts.
 * Liest `window.currentProject.paperlessTagId` automatisch.
 *
 * Da die Paperless-API keine Custom-Field-Filterung unterstuetzt, wird
 * ein grosser Batch geladen und client-seitig gefiltert.
 *
 * Ohne aktives Projekt (oder ohne paperlessTagId) werden alle Dokumente ungefiltert zurueckgegeben.
 */

// Cache fuer die numerische Field-ID von "paperless_tag_id"
let _cachedPaperlessTagFieldId: number | null | undefined = undefined

async function _resolvePaperlessTagFieldId(): Promise<number | null> {
  if (_cachedPaperlessTagFieldId !== undefined) return _cachedPaperlessTagFieldId
  try {
    const defs = await fetchCustomFieldDefs()
    const ptField = defs.results.find(cf => cf.name === 'paperless_tag_id')
    _cachedPaperlessTagFieldId = ptField ? ptField.id : null
  } catch {
    _cachedPaperlessTagFieldId = null
  }
  return _cachedPaperlessTagFieldId
}

export async function fetchProjectDocuments(
  params: PaperlessSearchParams = {},
): Promise<PaperlessListResponse<PaperlessDocument>> {
  const proj = (window as any).currentProject
  const tagId = proj?.paperlessTagId != null ? Number(proj.paperlessTagId) : null

  if (tagId == null) {
    // Kein Projekt-Tag → ungefiltert laden
    return fetchDocuments(params)
  }

  // Field-ID aufloesen (gecacht nach erstem Aufruf)
  const fieldId = await _resolvePaperlessTagFieldId()

  // Grossen Batch laden fuer client-seitige Filterung
  const res = await fetchDocuments({
    ...params,
    page: 1,
    page_size: 200,
  })

  const filtered = res.results.filter((doc) => {
    if (!doc.custom_fields) return false
    return doc.custom_fields.some(
      (cf) =>
        (cf.field_name === 'paperless_tag_id' || (fieldId != null && cf.field === fieldId)) &&
        String(cf.value) === String(tagId),
    )
  })

  // Client-seitige Paginierung
  const pageSize = params.page_size ?? 25
  const page = params.page ?? 1
  const start = (page - 1) * pageSize

  return {
    count: filtered.length,
    next: start + pageSize < filtered.length ? 'more' : null,
    previous: page > 1 ? 'prev' : null,
    results: filtered.slice(start, start + pageSize),
  }
}

/** Einzelnes Dokument abrufen — GET /paperless/documents/{id} */
export async function fetchDocument(id: number): Promise<PaperlessDocument> {
  const url = IS_DEV
    ? `/paperless-api/documents/${id}/`
    : `${API_BASE}/paperless/documents/${id}`

  const res = await fetch(url, { headers: headers() })
  if (!res.ok) throw new Error(`Paperless API error: ${res.status}`)
  return res.json()
}

/** Dokument-Thumbnail URL */
export function getDocumentThumbnailUrl(id: number): string {
  if (IS_DEV) return `/paperless-api/documents/${id}/thumb/`
  return `${API_BASE}/paperless/documents/${id}/thumb`
}

/** Dokument-Download URL — GET /paperless/documents/{doc_id}/download */
export function getDocumentDownloadUrl(id: number): string {
  if (IS_DEV) return `/paperless-api/documents/${id}/download/`
  return `${API_BASE}/paperless/documents/${id}/download`
}

/** Dokument-Preview URL — GET /paperless/documents/{doc_id}/preview */
export function getDocumentPreviewUrl(id: number): string {
  if (IS_DEV) return `/paperless-api/documents/${id}/preview/`
  return `${API_BASE}/paperless/documents/${id}/preview`
}

/** Dokument in Paperless-UI öffnen */
export function getDocumentWebUrl(id: number): string {
  return `https://paperless.administer.cloud/documents/${id}/details`
}

/** Tags auflisten */
export async function fetchTags(): Promise<PaperlessListResponse<PaperlessTag>> {
  const url = IS_DEV
    ? `/paperless-api/tags/?page_size=100`
    : `${API_BASE}/paperless/documents/tags/?page_size=100`

  const res = await fetch(url, { headers: headers() })
  if (!res.ok) throw new Error(`Paperless API error: ${res.status}`)
  return res.json()
}

/** Custom-Field-Definitionen auflisten */
export interface PaperlessCustomFieldDef {
  id: number
  name: string
  data_type: string
}

export async function fetchCustomFieldDefs(): Promise<PaperlessListResponse<PaperlessCustomFieldDef>> {
  const url = IS_DEV
    ? `/paperless-api/custom_fields/?page_size=100`
    : `${API_BASE}/paperless/documents/custom_fields/?page_size=100`

  const res = await fetch(url, { headers: headers() })
  if (!res.ok) throw new Error(`Paperless API error: ${res.status}`)
  return res.json()
}

/** Korrespondenten auflisten */
export async function fetchCorrespondents(): Promise<PaperlessListResponse<PaperlessCorrespondent>> {
  const url = IS_DEV
    ? `/paperless-api/correspondents/?page_size=100`
    : `${API_BASE}/paperless/documents/correspondents/?page_size=100`

  const res = await fetch(url, { headers: headers() })
  if (!res.ok) throw new Error(`Paperless API error: ${res.status}`)
  return res.json()
}

/** Dokumenttypen auflisten */
export async function fetchDocumentTypes(): Promise<PaperlessListResponse<PaperlessDocumentType>> {
  const url = IS_DEV
    ? `/paperless-api/document_types/?page_size=100`
    : `${API_BASE}/paperless/documents/document_types/?page_size=100`

  const res = await fetch(url, { headers: headers() })
  if (!res.ok) throw new Error(`Paperless API error: ${res.status}`)
  return res.json()
}

/**
 * Dokument hochladen — POST /paperless/upload
 * In Dev: direkt an Paperless /post_document/
 * In Prod: an FastAPI /paperless/upload (form-data: file, title, tag_ids, session_id)
 */
export async function uploadDocument(
  file: File,
  options?: {
    title?: string
    correspondent?: number
    document_type?: number
    tags?: number[]
    session_id?: string
  }
): Promise<PaperlessUploadResponse | string> {
  const formData = new FormData()

  if (IS_DEV) {
    // Dev: direkt an Paperless-ngx API
    formData.append('document', file)
    if (options?.title) formData.append('title', options.title)
    if (options?.correspondent) formData.append('correspondent', String(options.correspondent))
    if (options?.document_type) formData.append('document_type', String(options.document_type))
    if (options?.tags) {
      options.tags.forEach(t => formData.append('tags', String(t)))
    }

    const res = await fetch('/paperless-api/documents/post_document/', {
      method: 'POST',
      headers: headers(),
      body: formData,
    })

    if (!res.ok) {
      const errorText = await res.text()
      throw new Error(`Upload failed: ${res.status} — ${errorText}`)
    }
    return res.text()
  }

  // Prod: FastAPI-Proxy /paperless/upload
  formData.append('file', file)
  if (options?.title) formData.append('title', options.title)
  if (options?.tags?.length) formData.append('tag_ids', options.tags.join(','))
  if (options?.session_id) formData.append('session_id', options.session_id)

  const res = await fetch(`${API_BASE}/paperless/upload`, {
    method: 'POST',
    body: formData,
  })

  if (!res.ok) {
    const errorText = await res.text()
    throw new Error(`Upload failed: ${res.status} — ${errorText}`)
  }
  return res.json() as Promise<PaperlessUploadResponse>
}

/** Dokument-Titel/Metadaten aktualisieren */
export async function updateDocument(
  id: number,
  data: Partial<Pick<PaperlessDocument, 'title' | 'correspondent' | 'document_type' | 'tags'>> & {
    custom_fields?: Array<{ field: number; value: string | number | null }>
  }
): Promise<PaperlessDocument> {
  const url = IS_DEV
    ? `/paperless-api/documents/${id}/`
    : `${API_BASE}/paperless/documents/${id}`

  const res = await fetch(url, {
    method: 'PATCH',
    headers: jsonHeaders(),
    body: JSON.stringify(data),
  })
  if (!res.ok) throw new Error(`Paperless API error: ${res.status}`)
  return res.json()
}

/** Dokument löschen */
export async function deleteDocument(id: number): Promise<void> {
  const url = IS_DEV
    ? `/paperless-api/documents/${id}/`
    : `${API_BASE}/paperless/documents/${id}`

  const res = await fetch(url, {
    method: 'DELETE',
    headers: headers(),
  })
  if (!res.ok) throw new Error(`Paperless API error: ${res.status}`)
}

/** API erreichbar? */
export async function checkConnection(): Promise<boolean> {
  try {
    const url = IS_DEV ? '/paperless-api/' : `${API_BASE}/paperless/documents/`
    const res = await fetch(url, { headers: headers() })
    return res.ok
  } catch {
    return false
  }
}

// ── Neue Endpunkte (FastAPI-Spec) ──────────────────────────────────────

/**
 * Volltextsuche — GET /paperless/search
 * Separater Suchendpunkt mit eigenem Response-Format.
 */
export async function searchDocuments(
  query: string,
  page: number = 1,
  pageSize: number = 25
): Promise<PaperlessSearchResponse> {
  const qp = new URLSearchParams({ query, page: String(page), page_size: String(pageSize) })
  const url = IS_DEV
    ? `/paperless-api/documents/?query=${encodeURIComponent(query)}&page=${page}&page_size=${pageSize}`
    : `${API_BASE}/paperless/search?${qp.toString()}`

  const res = await fetch(url, { headers: headers() })
  if (!res.ok) throw new Error(`Paperless search error: ${res.status}`)
  return res.json()
}

/**
 * Task-Status abfragen — GET /paperless/tasks/{task_id}
 * Wird nach Upload zum Polling des OCR-Fortschritts verwendet.
 */
export async function fetchTaskStatus(taskId: string): Promise<PaperlessTaskStatus> {
  const url = `${API_BASE}/paperless/tasks/${taskId}`
  const res = await fetch(url)
  if (!res.ok) throw new Error(`Task status error: ${res.status}`)
  return res.json()
}

/**
 * Seitennummern für Suchbegriff — GET /paperless/documents/{doc_id}/pages?query=
 * Gibt die Seiten zurück, auf denen der Suchbegriff vorkommt.
 */
export async function fetchDocumentPages(
  docId: number,
  query: string
): Promise<PaperlessPageSearchResponse> {
  const url = `${API_BASE}/paperless/documents/${docId}/pages?query=${encodeURIComponent(query)}`
  const res = await fetch(url)
  if (!res.ok) throw new Error(`Page search error: ${res.status}`)
  return res.json()
}

/**
 * Custom Fields befüllen — POST /paperless/documents/{doc_id}/fields
 * Variante A: { field_id, value } — einzelnes Feld per ID
 * Variante B: { fields: { Name: Wert, ... } } — mehrere Felder per Name
 */
export async function updateDocumentFields(
  docId: number,
  data: { field_id: number; value: string } | { fields: Record<string, string> }
): Promise<{ ok: boolean; document_id: number; updated: Record<string, string> }> {
  const url = `${API_BASE}/paperless/documents/${docId}/fields`
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!res.ok) throw new Error(`Fields update error: ${res.status}`)
  return res.json()
}

/** Paperless Base-URL (für externe Links — immer die echte URL) */
export const PAPERLESS_BASE_URL = 'https://paperless.administer.cloud'
